import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, X, FileJson, FileSpreadsheet, Printer } from 'lucide-react';

const ExportModal = ({ transaction, codes, account, onClose }) => {
  const [exportType, setExportType] = useState('pdf');
  const [exporting, setExporting] = useState(false);

  const exportFormats = [
    { id: 'pdf', name: 'PDF Document', icon: FileText, color: 'text-red-400' },
    { id: 'json', name: 'JSON Data', icon: FileJson, color: 'text-yellow-400' },
    { id: 'csv', name: 'CSV Spreadsheet', icon: FileSpreadsheet, color: 'text-green-400' },
  ];

  const generatePDF = () => {
    const content = `
╔═══════════════════════════════════════════════════════════╗
║           CYBERBANK TERMINAL - TRANSACTION RECEIPT        ║
╚═══════════════════════════════════════════════════════════╝

TRANSACTION DETAILS
───────────────────────────────────────────────────────────
Reference:        ${transaction.transaction_reference}
Type:             ${transaction.transfer_type}
Amount:           $${transaction.amount.toLocaleString()} ${transaction.currency}
Status:           ${transaction.status}
Date:             ${new Date(transaction.created_at).toLocaleString()}
${transaction.completed_at ? `Completed:        ${new Date(transaction.completed_at).toLocaleString()}` : ''}

ACCOUNT INFORMATION
───────────────────────────────────────────────────────────
Account Number:   ${account?.account_number || 'N/A'}
IBAN:             ${account?.iban || 'N/A'}
Bank:             ${account?.bank_name || 'N/A'}
SWIFT:            ${account?.swift_code || 'N/A'}

RECIPIENT INFORMATION
───────────────────────────────────────────────────────────
Recipient:        ${transaction.recipient_info || 'N/A'}

${codes ? `
TRANSACTION CODES
───────────────────────────────────────────────────────────
Transaction Code:         ${codes.transaction_code || 'N/A'}
Transfer Code:            ${codes.transfer_code || 'N/A'}
Access Code:              ${codes.access_code || 'N/A'}
Activation Code:          ${codes.activation_code || 'N/A'}
IMAD Number:              ${codes.imad_number || 'N/A'}
UTR:                      ${codes.utr || 'N/A'}
Sort Code:                ${codes.sort_code || 'N/A'}
` : ''}

SECURITY & COMPLIANCE
───────────────────────────────────────────────────────────
Encryption:       AES-256-GCM
Authentication:   JWT Token
Compliance:       ISO 27001, PCI DSS
Security Level:   MAXIMUM

═══════════════════════════════════════════════════════════
Generated by CYBERBANK TERMINAL v2.0
Date: ${new Date().toLocaleString()}
This is a digitally generated receipt
═══════════════════════════════════════════════════════════
    `.trim();

    return content;
  };

  const generateJSON = () => {
    return JSON.stringify({
      transaction: {
        reference: transaction.transaction_reference,
        type: transaction.transfer_type,
        amount: transaction.amount,
        currency: transaction.currency,
        status: transaction.status,
        recipient: transaction.recipient_info,
        created_at: transaction.created_at,
        completed_at: transaction.completed_at
      },
      account: account ? {
        account_number: account.account_number,
        iban: account.iban,
        bank_name: account.bank_name,
        swift_code: account.swift_code
      } : null,
      codes: codes,
      metadata: {
        exported_at: new Date().toISOString(),
        system: 'CYBERBANK TERMINAL v2.0',
        security_level: 'MAXIMUM'
      }
    }, null, 2);
  };

  const generateCSV = () => {
    const rows = [
      ['Field', 'Value'],
      ['Transaction Reference', transaction.transaction_reference],
      ['Transfer Type', transaction.transfer_type],
      ['Amount', `${transaction.amount} ${transaction.currency}`],
      ['Status', transaction.status],
      ['Recipient', transaction.recipient_info],
      ['Created At', new Date(transaction.created_at).toLocaleString()],
      ['Completed At', transaction.completed_at ? new Date(transaction.completed_at).toLocaleString() : 'N/A'],
      ['Account Number', account?.account_number || 'N/A'],
      ['IBAN', account?.iban || 'N/A'],
      ['Bank', account?.bank_name || 'N/A'],
    ];

    if (codes) {
      rows.push(['Transaction Code', codes.transaction_code || 'N/A']);
      rows.push(['Transfer Code', codes.transfer_code || 'N/A']);
      rows.push(['Access Code', codes.access_code || 'N/A']);
      rows.push(['IMAD Number', codes.imad_number || 'N/A']);
      rows.push(['UTR', codes.utr || 'N/A']);
    }

    return rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\\n');
  };

  const handleExport = () => {
    setExporting(true);

    setTimeout(() => {
      let content, filename, mimeType;

      switch(exportType) {
        case 'pdf':
          content = generatePDF();
          filename = `transaction_${transaction.transaction_reference}.txt`;
          mimeType = 'text/plain';
          break;
        case 'json':
          content = generateJSON();
          filename = `transaction_${transaction.transaction_reference}.json`;
          mimeType = 'application/json';
          break;
        case 'csv':
          content = generateCSV();
          filename = `transaction_${transaction.transaction_reference}.csv`;
          mimeType = 'text/csv';
          break;
        default:
          content = generatePDF();
          filename = `transaction_${transaction.transaction_reference}.txt`;
          mimeType = 'text/plain';
      }

      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setExporting(false);
      setTimeout(() => onClose(), 500);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl terminal-card p-8"
      >
        <div className="flex items-center justify-between mb-6 border-b border-terminal-green pb-4">
          <div className="flex items-center gap-3">
            <Printer className="w-6 h-6 text-terminal-green" />
            <h2 className="text-2xl font-bold cyber-font">EXPORT TRANSACTION</h2>
          </div>
          <button
            onClick={onClose}
            className="text-terminal-green-dim hover:text-terminal-green"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-terminal-green-dim text-sm mb-4">
            Select export format for transaction {transaction.transaction_reference}
          </p>

          <div className="grid grid-cols-3 gap-4">
            {exportFormats.map(format => {
              const Icon = format.icon;
              return (
                <motion.button
                  key={format.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setExportType(format.id)}
                  className={`p-6 border-2 transition-all ${
                    exportType === format.id
                      ? 'border-terminal-green bg-terminal-green/20'
                      : 'border-terminal-green/30 hover:border-terminal-green/60'
                  }`}
                >
                  <Icon className={`w-8 h-8 mx-auto mb-2 ${format.color}`} />
                  <div className="text-sm font-bold">{format.name}</div>
                </motion.button>
              );
            })}
          </div>
        </div>

        <div className="terminal-card p-4 mb-6 text-xs">
          <div className="text-terminal-green-dim mb-2">PREVIEW:</div>
          <div className="font-mono text-terminal-green">
            {exportType === 'pdf' && 'PDF format with full transaction details and codes'}
            {exportType === 'json' && 'Structured JSON data suitable for API integration'}
            {exportType === 'csv' && 'Spreadsheet format compatible with Excel/Numbers'}
          </div>
        </div>

        <div className="flex gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExport}
            disabled={exporting}
            className="flex-1 py-3 terminal-button flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {exporting ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Download className="w-5 h-5" />
                </motion.div>
                <span>EXPORTING...</span>
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                <span>EXPORT {exportType.toUpperCase()}</span>
              </>
            )}
          </motion.button>
          <button
            onClick={onClose}
            className="px-6 py-3 terminal-button"
          >
            CANCEL
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ExportModal;
